import Button, { ButtonProps } from "@bmi-digital/components/button";
import Typography from "@bmi-digital/components/typography";
import { replaceSpaces } from "@bmi-digital/components/utils";
import { microCopy } from "@bmi-digital/dxb-microcopies";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { devLog } from "../../utils/devLog";
import withGTM from "../../utils/google-tag-manager";
import { useIsMobileDevice } from "../../utils/useIsMobileDevice";
import ProgressIndicator from "../ProgressIndicator";
import RichText from "../RichText";
import { useSiteContext } from "../Site";
import { TitleWithContentData as TitleWithContentType } from "../TitleWithContent";
import { GutteringSelections } from "./_Guttering";
import { createPdf } from "./_PDF";
import {
  StyledFieldContainer,
  StyledFormSection,
  StyledFormSectionTypographyHelp,
  StyledNeedHelpSection,
  StyledNeedHelpSectionTitle,
  StyledPrintReportSection,
  StyledResetLinkButton,
  StyledSpinnerContainer,
  StyledTypographyHelp
} from "./_Results.styles";
import QuantitiesCalculator from "./calculation/QuantitiesCalculator";
import { battenCalc } from "./calculation/calculate";
import { CONTINGENCY_PERCENTAGE_TEXT } from "./calculation/constants";
import Alert from "./subcomponents/_Alert";
import FieldContainer from "./subcomponents/_FieldContainer";
import QuantityTable from "./subcomponents/quantity-table/QuantityTable";
import useRoofData from "./subcomponents/selected-roof/hooks/useRoofData";
import type {
  BasicResults,
  ResultsRow,
  Tile,
  TileOptionSelections,
  Underlay
} from "./types";
import type {
  DimensionsValues,
  Line,
  LinesMap,
  Measurements,
  RoofV2 as Roof
} from "./types/roof";

const GTMButton = withGTM<ButtonProps>(Button);

type PrintReportSectionProps = {
  results: BasicResults;
  area: number;
  hubSpotFormId: string | null;
  setIsHubSpotFormAvailable: (value: boolean) => void;
  isHubSpotFormAvailable: boolean;
  needHelpSection: TitleWithContentType;
  setLoading: (value: boolean) => void;
  roof: Roof;
  dimensions: DimensionsValues;
};

const ALLOWED_CONTENT_TYPES = ["image/jpeg", "image/png"];

export const replaceImageURLWithImage = async (
  result: ResultsRow
): Promise<ResultsRow> => {
  let dataURI: string | undefined = undefined;
  try {
    if (!result.image) {
      return result;
    }

    const response = await fetch(result.image);
    const contentType = response.headers.get("content-type");

    if (contentType && ALLOWED_CONTENT_TYPES.includes(contentType)) {
      const imageArrayBuffer = await response.arrayBuffer();
      const imageUInt8Array = new Uint8Array(imageArrayBuffer);
      let utf8EncodedImage = "";
      for (const byte of imageUInt8Array) {
        utf8EncodedImage += String.fromCharCode(byte);
      }
      const base64EncodedImage = btoa(utf8EncodedImage);
      dataURI = `data:${contentType};base64,${base64EncodedImage}`;
    }
  } catch (error) {
    devLog("Failed to convert image for PDF", result, error);
  }

  return {
    ...result,
    image: dataURI
  };
};

const PrintReportSection = ({
  results,
  area,
  hubSpotFormId,
  setIsHubSpotFormAvailable,
  isHubSpotFormAvailable,
  needHelpSection,
  setLoading,
  roof,
  dimensions
}: PrintReportSectionProps) => {
  const { getMicroCopy } = useSiteContext();
  const [hubSpotForm, setHubSpotForm] = useState<HTMLIFrameElement | null>(
    null
  );
  const isMobileDevice = useIsMobileDevice();
  const roofDimensionsData = useRoofData({ dimensions });

  const getPDFReport = useCallback(async () => {
    try {
      const resultsWithImages = { ...results };

      for (const category of Object.keys(results)) {
        // eslint-disable-next-line security/detect-object-injection
        resultsWithImages[category as keyof typeof results] = await Promise.all(
          // eslint-disable-next-line security/detect-object-injection
          results[category as keyof typeof results].map(
            replaceImageURLWithImage
          )
        );
      }

      return createPdf({
        roof,
        dimensions: roofDimensionsData,
        results: resultsWithImages,
        area: (area / 10000).toFixed(2),
        getMicroCopy
      });
    } catch (err) {
      devLog("Failed to generate PDF", err);
    }
  }, [results, area, getMicroCopy, roof, roofDimensionsData]);

  const addPdfReportToHSForm = async () => {
    if (hubSpotForm) {
      const pdfReport = await getPDFReport();
      const fileInput =
        hubSpotForm.contentDocument.querySelector<HTMLInputElement>(
          "input[type=file]"
        );

      if (fileInput) {
        pdfReport.getBlob((blob) => {
          const file = new File([blob], "Calculation-report.pdf", {
            type: "application/pdf"
          });

          const list = new DataTransfer();
          list.items.add(file);
          fileInput.files = list.files;
        });
      }
    }
  };

  useEffect(() => {
    addPdfReportToHSForm();
  }, [addPdfReportToHSForm]);

  const openPdfReport = async () => {
    const pdfReport = await getPDFReport();
    // If user uses mobile device we should open the report in the same tab,
    // because opening in a separate tab doesn't work properly for all mobile devices
    if (isMobileDevice) {
      pdfReport?.open(undefined, window);
      return;
    }

    pdfReport.open();
  };

  const onFormReady = (_, hsForm: HTMLIFrameElement) => {
    setLoading(false);
    setHubSpotForm(hsForm);
    const styles = document.createElement("style");
    // Hides file input inside the iframe form.
    styles.textContent = ".hs_file, .hs-fieldtype-file {  display: none; }";
    hsForm.contentDocument.head.appendChild(styles);
  };

  const onFormLoadError = () => {
    setIsHubSpotFormAvailable(false);
    setLoading(false);
  };

  return isHubSpotFormAvailable ? (
    <StyledFormSection
      id="webtool-calculator-form-id"
      backgroundColor="white"
      onSuccess={openPdfReport}
      onFormReady={onFormReady}
      onFormLoadError={onFormLoadError}
      data={{
        hubSpotFormGuid: hubSpotFormId,
        showTitle: true,
        title: getMicroCopy(microCopy.RESULTS_EMAIL_TITLE),
        source: "HubSpot",
        inputs: null,
        recipients: null,
        submitText: null,
        successRedirect: null,
        emailSubjectFormat: null,
        richTextDescription: null
      }}
      customDescriptionElement={
        <StyledFormSectionTypographyHelp>
          {getMicroCopy(microCopy.RESULTS_EMAIL_HELP)}
        </StyledFormSectionTypographyHelp>
      }
    />
  ) : (
    <StyledPrintReportSection id="print-calculations-report">
      <div>
        <Typography variant="h4" hasUnderline>
          {getMicroCopy(microCopy.RESULTS_EMAIL_TITLE)}
        </Typography>
        <StyledTypographyHelp>
          {getMicroCopy(microCopy.RESULTS_DOWNLOAD_PDF_HELP)}
        </StyledTypographyHelp>
        <GTMButton
          onClick={openPdfReport}
          gtm={{
            id: "rc-solution",
            label: getMicroCopy(microCopy.RESULTS_DOWNLOAD_PDF_LABEL),
            action: "selected"
          }}
        >
          {getMicroCopy(microCopy.RESULTS_DOWNLOAD_PDF_LABEL)}
        </GTMButton>
      </div>
      <StyledNeedHelpSection
        backgroundColor="pearl"
        hasNoPadding
        data-testid={`print-calculcation-report${replaceSpaces(
          needHelpSection.title
        )}`}
      >
        {needHelpSection.title && (
          <StyledNeedHelpSectionTitle>
            {needHelpSection.title}
          </StyledNeedHelpSectionTitle>
        )}
        <RichText document={needHelpSection.content} />
      </StyledNeedHelpSection>
    </StyledPrintReportSection>
  );
};

type SetRows = React.Dispatch<React.SetStateAction<Array<ResultsRow>>>;

export type ResultProps = {
  isDebugging?: boolean;
  measurements: Measurements;
  variant: Tile;
  tileOptions: TileOptionSelections;
  underlay: Underlay;
  guttering?: GutteringSelections;
  hubSpotFormId: string | null;
  setIsHubSpotFormAvailable: (value: boolean) => void;
  isHubSpotFormAvailable: boolean;
  needHelpSection: TitleWithContentType;
  roof: Roof;
  dimensions: DimensionsValues;
};

const Results = ({
  isDebugging,
  measurements,
  variant,
  tileOptions,
  underlay,
  guttering,
  hubSpotFormId,
  setIsHubSpotFormAvailable,
  isHubSpotFormAvailable,
  needHelpSection,
  roof,
  dimensions
}: ResultProps) => {
  const { getMicroCopy } = useSiteContext();
  const [loading, setLoading] = useState<boolean>(isHubSpotFormAvailable);
  const [showNeedToKnowAlert, setShowNeedToKnowAlert] = useState<boolean>(true);
  const [showQuantityAlert, setShowQuantityAlert] = useState<boolean>(true);
  const [updatedProductsList, setUpdatedProductsList] = useState<Set<string>>(
    new Set()
  );

  const { faces, lines, area } = measurements;

  const results = useMemo(() => {
    const verge = tileOptions.verge === "none" ? undefined : tileOptions.verge;
    const ventilationHoods =
      tileOptions.ventilationHoods === "none"
        ? []
        : tileOptions.ventilationHoods;

    const quantitiesCalculator = new QuantitiesCalculator({
      measurements,
      mainTileVariant: variant,
      vergeOption: verge,
      ridge: tileOptions.ridge,
      ventilationHoods: ventilationHoods,
      underlay,
      gutteringVariant: guttering?.gutteringVariant,
      gutteringHook: guttering?.gutteringHook,
      downPipes: guttering?.downPipes,
      downPipeConnectors: guttering?.downPipeConnectors,
      roof
    });

    return quantitiesCalculator.getResultsRowsByCategory();
  }, [
    guttering?.downPipeConnectors,
    guttering?.downPipes,
    guttering?.gutteringHook,
    guttering?.gutteringVariant,
    measurements,
    tileOptions.ridge,
    tileOptions.ventilationHoods,
    tileOptions.verge,
    underlay,
    variant,
    roof
  ]);

  const [tileRows, setTileRows] = useState(results.tiles);
  const [fixingRows, setFixingRows] = useState(results.fixings);
  const [sealingRows, setSealingRows] = useState(results.sealing);
  const [ventilationRows, setVentilationRows] = useState(results.ventilation);
  const [accessoryRows, setAccessoryRows] = useState(results.accessories);

  const resetProducts = () => {
    setTileRows(results.tiles);
    setFixingRows(results.fixings);
    setSealingRows(results.sealing);
    setVentilationRows(results.ventilation);
    setAccessoryRows(results.accessories);
    setUpdatedProductsList(new Set());
  };

  const tableLabels = useMemo(
    () => ({
      title: getMicroCopy(microCopy.RESULTS_TABLE_TITLE),
      packSize: getMicroCopy(microCopy.RESULTS_TABLE_PACK_SIZE),
      externalProductCode: getMicroCopy(
        microCopy.RESULTS_TABLE_EXTERNAL_PRODUCT_CODE
      ),
      quantity: getMicroCopy(microCopy.RESULTS_TABLE_QUANTITY),
      remove: getMicroCopy(microCopy.RESULTS_TABLE_REMOVE)
    }),
    [getMicroCopy]
  );

  const handleUpdatedProductsList = (
    productId: string,
    action: "add" | "delete"
  ) => {
    if (action === "add") {
      setUpdatedProductsList((prev) => {
        const next = new Set(prev);
        next.add(productId);
        return next;
      });

      return;
    }

    setUpdatedProductsList((prev) => {
      const next = new Set(prev);
      next.delete(productId);
      return next;
    });
  };

  const deleteRow = (setRaw: SetRows) => (itemToDelete: ResultsRow) => {
    setRaw((prevProducts) =>
      prevProducts.filter(
        (product) =>
          product.externalProductCode !== itemToDelete.externalProductCode
      )
    );

    handleUpdatedProductsList(itemToDelete.externalProductCode, "add");
  };

  const changeQuantity =
    (setRows: SetRows, originalRows: ResultsRow[]) =>
    (item: ResultsRow, newQuantity: number) => {
      setRows((prevProducts) =>
        prevProducts.map((product) =>
          product.externalProductCode === item.externalProductCode
            ? { ...product, quantity: newQuantity }
            : product
        )
      );

      const hasQuantityChanged = originalRows.find(
        (product) =>
          product.externalProductCode === item.externalProductCode &&
          product.quantity !== newQuantity
      );

      handleUpdatedProductsList(
        item.externalProductCode,
        hasQuantityChanged ? "add" : "delete"
      );
    };

  const handleCloseNeedToKnowAlert = useCloseAlertHandler(
    setShowNeedToKnowAlert
  );
  const handleCloseQuantityAlert = useCloseAlertHandler(setShowQuantityAlert);
  const closeButtonLabel = getMicroCopy(microCopy.GLOBAL_CLOSE);
  const quantitiesResetButtonLabel = getMicroCopy(
    microCopy.RESULTS_ALERTS_QUANTITIES_RESET_BUTTON
  );

  return (
    <>
      {loading && (
        <StyledSpinnerContainer>
          <ProgressIndicator size={40} />
        </StyledSpinnerContainer>
      )}
      <div>
        {tileRows.length ? (
          <StyledFieldContainer
            title={getMicroCopy(microCopy.RESULTS_CATEGORIES_TITLES)}
          >
            <QuantityTable
              onDelete={deleteRow(setTileRows)}
              onChangeQuantity={changeQuantity(setTileRows, results.tiles)}
              rows={tileRows}
              {...tableLabels}
            />
          </StyledFieldContainer>
        ) : null}
        {fixingRows.length ? (
          <StyledFieldContainer
            title={getMicroCopy(microCopy.RESULTS_CATEGORIES_FIXINGS)}
          >
            <QuantityTable
              onDelete={deleteRow(setFixingRows)}
              onChangeQuantity={changeQuantity(setFixingRows, results.fixings)}
              rows={fixingRows}
              {...tableLabels}
            />
          </StyledFieldContainer>
        ) : null}
        {ventilationRows.length ? (
          <StyledFieldContainer
            title={getMicroCopy(microCopy.RESULTS_CATEGORIES_VENTILATION)}
          >
            <QuantityTable
              onDelete={deleteRow(setVentilationRows)}
              onChangeQuantity={changeQuantity(
                setVentilationRows,
                results.ventilation
              )}
              rows={ventilationRows}
              {...tableLabels}
            />
          </StyledFieldContainer>
        ) : null}
        {sealingRows.length ? (
          <StyledFieldContainer
            title={getMicroCopy(microCopy.RESULTS_CATEGORIES_SEALING)}
          >
            <QuantityTable
              onDelete={deleteRow(setSealingRows)}
              onChangeQuantity={changeQuantity(setSealingRows, results.sealing)}
              rows={sealingRows}
              {...tableLabels}
            />
          </StyledFieldContainer>
        ) : null}
        {accessoryRows.length ? (
          <StyledFieldContainer
            title={getMicroCopy(microCopy.RESULTS_CATEGORIES_ACCESSORIES)}
          >
            <QuantityTable
              onDelete={deleteRow(setAccessoryRows)}
              onChangeQuantity={changeQuantity(
                setAccessoryRows,
                results.accessories
              )}
              rows={accessoryRows}
              {...tableLabels}
            />
          </StyledFieldContainer>
        ) : null}
        {showQuantityAlert &&
          (updatedProductsList.size ? (
            <Alert
              type="warning"
              title={getMicroCopy(
                microCopy.RESULTS_ALERTS_QUANTITIES_UPDATED_TITLE
              )}
              first
              closeButtonProps={{
                accessibilityLabel: closeButtonLabel,
                gtm: {
                  id: "cta-click1",
                  label: closeButtonLabel
                },
                onClick: handleCloseQuantityAlert,
                "data-testid": "alert-close-button-quantity-updated"
              }}
            >
              {getMicroCopy(microCopy.RESULTS_ALERTS_QUANTITIES_UPDATED_TEXT)}{" "}
              <StyledResetLinkButton
                onClick={resetProducts}
                variant="text"
                gtm={{
                  id: "cta-click1",
                  label: quantitiesResetButtonLabel
                }}
              >
                {quantitiesResetButtonLabel}
              </StyledResetLinkButton>
            </Alert>
          ) : (
            <Alert
              type="warning"
              title={getMicroCopy(
                microCopy.RESULTS_ALERTS_QUANTITIES_NOT_UPDATED_TITLE
              )}
              first
              closeButtonProps={{
                accessibilityLabel: closeButtonLabel,
                onClick: handleCloseQuantityAlert,
                "data-testid": "alert-close-button-quantity-not-updated",
                gtm: {
                  id: "cta-click1",
                  label: closeButtonLabel
                }
              }}
            >
              {getMicroCopy(
                microCopy.RESULTS_ALERTS_QUANTITIES_NOT_UPDATED_TEXT
              )}
            </Alert>
          ))}

        {showNeedToKnowAlert && (
          <Alert
            title={getMicroCopy(microCopy.RESULTS_ALERTS_NEED_TO_KNOW_TITLE)}
            last
            closeButtonProps={{
              accessibilityLabel: closeButtonLabel,
              onClick: handleCloseNeedToKnowAlert,
              "data-testid": "alert-close-button-needToKnow",
              gtm: {
                id: "cta-click1",
                label: closeButtonLabel
              }
            }}
          >
            {getMicroCopy(microCopy.RESULTS_ALERTS_NEED_TO_KNOW_TEXT, {
              contingency: CONTINGENCY_PERCENTAGE_TEXT
            })}
          </Alert>
        )}
        <PrintReportSection
          results={{
            tiles: tileRows,
            fixings: fixingRows,
            sealing: sealingRows,
            ventilation: ventilationRows,
            accessories: accessoryRows
          }}
          area={area || 0}
          hubSpotFormId={hubSpotFormId}
          setIsHubSpotFormAvailable={setIsHubSpotFormAvailable}
          isHubSpotFormAvailable={isHubSpotFormAvailable}
          needHelpSection={needHelpSection}
          setLoading={setLoading}
          dimensions={dimensions}
          roof={roof}
        />
        {isDebugging ? (
          <FieldContainer>
            <Typography variant="h3">
              Measurements (showing because debugging mode is ON)
            </Typography>
            <Typography variant="h4">Lines</Typography>
            <ul>
              {Object.keys(lines).map((l) =>
                // eslint-disable-next-line security/detect-object-injection
                lines[l as keyof LinesMap].length ? (
                  <li key={l}>
                    <b>{l}:</b>{" "}
                    {/* eslint-disable-next-line security/detect-object-injection */}
                    {(lines[l as keyof LinesMap] as Line[])
                      .map((v) => v.length.toFixed(2))
                      .join(" | ")}
                  </li>
                ) : null
              )}
            </ul>
            <Typography variant="h4">Faces</Typography>
            <ul>
              {faces.map((face, i) => (
                <li key={i}>
                  <b>face {i + 1} battens (width):</b>{" "}
                  {battenCalc({
                    vertices: face.vertices,
                    pitch: face.pitch,
                    mainTileVariant: variant,
                    subtract: Boolean(face.subtract)
                  })
                    .map(({ width }) => width.toFixed(2))
                    .join(" | ")}
                </li>
              ))}
            </ul>
          </FieldContainer>
        ) : null}
      </div>
    </>
  );
};

export default Results;

const useCloseAlertHandler = (
  setter: React.Dispatch<React.SetStateAction<boolean>>
) => {
  return useCallback(() => {
    setter((prevCloseAlert) => !prevCloseAlert);
  }, [setter]);
};
